import React from 'react';
import styled, { css } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $width?: 'fit-content' | 'full';
  $height?: number;
  $variant?: 'default' | 'gradation' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  $size?: 'default' | 'xxs' | 'xs' | 'sm' | 'lg' | 'full' | 'icon';
}

const getVariantStyles = (variant: ButtonProps['$variant'] = 'default') => {
  switch (variant) {
    case 'default':
      return css`
        background-color: ${semanticColors.background.brand};
        color: ${semanticColors.text.white};
        &:hover {
          background-color: ${semanticColors.background.brandHover};
        }
      `;
    case 'gradation':
      return css`
        background: linear-gradient(90deg, #0F172A 0%, #1E293B 100%);
        color: ${semanticColors.text.primary};
        &:hover {
          background: ${semanticColors.background.brandHover};
          opacity: 0.9;
        }
      `;
    case 'destructive':
      return css`
        background-color: ${semanticColors.background.danger.primary};
        color: ${semanticColors.background.secondary};
        &:hover {
          background-color: ${semanticColors.background.danger.primaryHover};
        }
      `;
    case 'outline':
      return css`
        border: 1px solid ${semanticColors.background.primary};
        background-color: ${semanticColors.background.secondary};
        &:hover {
          background-color: ${semanticColors.background.primaryHover};
          color: ${semanticColors.text.primary};
        }
      `;
    case 'secondary':
      return css`
        background-color: ${semanticColors.background.secondary};
        color: ${semanticColors.text.primary};
        &:hover {
          background-color: ${semanticColors.background.secondaryHover};
        }
      `;
    case 'ghost':
      return css`
        background-color: transparent;
        &:hover {
          background-color: ${semanticColors.background.primaryHover};
          color: ${semanticColors.text.primary};
        }
      `;
    case 'link':
      return css`
        color: ${semanticColors.background.brand};
        text-decoration-line: underline;
        text-underline-offset: 4px;
        background-color: transparent;
        &:hover {
          text-decoration-line: none;
        }
      `;
    default:
      return '';
  }
};

const getSizeStyles = (size: ButtonProps['$size'] = 'default') => {
  switch (size) {
    case 'default':
      return css`
        height: 2.5rem;
        padding: 0.5rem 1rem;
      `;
    case 'xxs':
      return css`
        height: 1.5rem;
        padding: 0.25rem 0.5rem;
      `;
    case 'xs':
      return css`
        height: 2rem;
        padding: 0.25rem 0.5rem;
      `;
    case 'sm':
      return css`
        height: 2.25rem;
        padding: 0.375rem 0.75rem;
      `;
    case 'lg':
      return css`
        height: 2.75rem;
        padding: 0.75rem 2rem;
      `;
    case 'full':
      return css`
        width: 100%;
        height: 2.25rem;
        padding: 0.375rem 0.75rem;
      `;
    case 'icon':
      return css`
        height: 2.5rem;
        width: 2.5rem;
        padding: 0.5rem;
      `;
    default:
      return '';
  }
};

const StyledButton = styled.button<ButtonProps>`
  width: ${props => props.$width === 'full' ? '100%' : 'fit-content'};
  height: ${props => props.$height ? `${props.$height}px` : 'auto'};
  min-height: ${props => props.$height ? `${props.$height}px` : 'auto'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  border: none;
  outline: none;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${semanticColors.background.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  ${props => getVariantStyles(props.$variant)}
  ${props => getSizeStyles(props.$size)}
`;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, $variant = 'default', $size = 'default', $width = 'fit-content', ...props }, ref) => {
    return (
      <StyledButton ref={ref} $variant={$variant} $size={$size} $width={$width} {...props}>
        {children}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';

export { Button };

